import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaBook,
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaEuroSign,
  FaInfoCircle,
  FaListOl,
  FaSave,
  FaTimes,
  FaTrash,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";

import {
  buildGroupPayload,
  COLOR_OPTIONS,
  EMPTY_GROUP_FORM,
  mapGroupToFormState,
  QUILL_FORMATS,
  QUILL_MODULES,
  WEEK_DAYS,
  toBool,
} from "../utils/groupsSettingsUtils";

function Field({ label, icon, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        ) : null}

        {children}
      </div>

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function SectionCard({ title, icon, children, description }) {
  return (
    <section className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="mb-4">
        <h3 className="flex items-center gap-2 text-base font-black text-slate-900 dark:text-white">
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
            {icon}
          </span>
          {title}
        </h3>

        {description ? (
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {description}
          </p>
        ) : null}
      </div>

      {children}
    </section>
  );
}

export default function GroupFormDrawer({
  isOpen,
  mode,
  group,
  defaultDojoId,
  nextOrder,
  dojos,
  enseignants,
  disciplineSuggestions,
  submitting,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(EMPTY_GROUP_FORM);
  const [horaires, setHoraires] = useState([{ jour: "", debut: "", fin: "" }]);
  const [enseignantsList, setEnseignantsList] = useState([""]);
  const [infoGroupe, setInfoGroupe] = useState("");
  const [modeAge, setModeAge] = useState("annees");
  const [ageMin, setAgeMin] = useState("");
  const [anneesSelectionnees, setAnneesSelectionnees] = useState([]);

  const currentYear = new Date().getFullYear();

  const listeAnnees = useMemo(() => {
    return Array.from({ length: 20 }, (_, index) => currentYear - index);
  }, [currentYear]);

  useEffect(() => {
    if (!isOpen) return;

    const state = mapGroupToFormState(group, {
      nextOrder,
      defaultDojoId,
    });

    setFormData(state.formData);
    setHoraires(state.horaires);
    setEnseignantsList(state.enseignantsList);
    setInfoGroupe(state.infoGroupe);
    setModeAge(state.modeAge);
    setAgeMin(state.ageMin);
    setAnneesSelectionnees(state.anneesSelectionnees);
  }, [isOpen, group, nextOrder, defaultDojoId]);

  const title =
    mode === "edit"
      ? "Modifier le groupe"
      : mode === "duplicate"
        ? "Dupliquer le groupe"
        : "Créer un groupe";

  const subtitle =
    mode === "duplicate"
      ? "Vérifiez les informations puis enregistrez la copie du groupe."
      : "Renseignez le dojo, les horaires, les enseignants, les tarifs et les quotas.";

  const canSubmit =
    String(formData.nom || "").trim() &&
    String(formData.dojo_id || "").trim() &&
    !submitting;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const addHoraire = () => {
    if (horaires.length >= 6) return;

    setHoraires((prev) => [...prev, { jour: "", debut: "", fin: "" }]);
  };

  const updateHoraire = (index, field, value) => {
    setHoraires((prev) =>
      prev.map((horaire, itemIndex) =>
        itemIndex === index
          ? {
              ...horaire,
              [field]: value,
            }
          : horaire,
      ),
    );
  };

  const removeHoraire = (index) => {
    setHoraires((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const addEnseignant = () => {
    if (enseignantsList.length >= 6) return;

    setEnseignantsList((prev) => [...prev, ""]);
  };

  const updateEnseignant = (index, value) => {
    setEnseignantsList((prev) =>
      prev.map((teacher, itemIndex) => (itemIndex === index ? value : teacher)),
    );
  };

  const removeEnseignant = (index) => {
    setEnseignantsList((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
  };

  const toggleYear = (year, checked) => {
    setAnneesSelectionnees((prev) => {
      if (checked) return [...prev, year];
      return prev.filter((item) => item !== year);
    });
  };

  const handleColorChange = (colorValue) => {
    setFormData((prev) => ({
      ...prev,
      color_groupe: colorValue,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    const payload = buildGroupPayload({
      formData,
      horaires,
      enseignantsList,
      infoGroupe,
      modeAge,
      ageMin,
      anneesSelectionnees,
    });

    onSubmit?.(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Groupe
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <SectionCard
              title="Informations principales"
              icon={<FaUsers />}
              description="Ces informations servent à afficher le groupe sur le site et à l’inscription."
            >
              <div className="grid gap-4 lg:grid-cols-3">
                <Field label="Dojo *" icon={<FaBuilding />}>
                  <select
                    name="dojo_id"
                    value={formData.dojo_id}
                    onChange={handleChange}
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  >
                    <option value="">Sélectionner un dojo</option>
                    {dojos.map((dojo) => (
                      <option key={dojo.id} value={dojo.id}>
                        {dojo.dojo_nom}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Nom du groupe *" icon={<FaUsers />}>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    placeholder="Ex : Baby Judo, Cadets, Adultes..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>

                <Field label="Discipline" icon={<FaBook />}>
                  <input
                    type="text"
                    name="discipline_groupe"
                    list="group-discipline-list"
                    value={formData.discipline_groupe}
                    onChange={handleChange}
                    placeholder="Ex : Judo, Taïso, Jujitsu..."
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />

                  <datalist id="group-discipline-list">
                    {disciplineSuggestions.map((discipline) => (
                      <option key={discipline} value={discipline} />
                    ))}
                  </datalist>
                </Field>
              </div>
            </SectionCard>

            <SectionCard
              title="Horaires"
              icon={<FaCalendarAlt />}
              description="Vous pouvez renseigner jusqu’à 6 créneaux horaires pour un même groupe."
            >
              <div className="space-y-3">
                {horaires.map((horaire, index) => (
                  <div
                    key={index}
                    className="grid gap-2 rounded-2xl bg-slate-50 p-3 dark:bg-slate-900 lg:grid-cols-[160px,1fr,1fr,auto]"
                  >
                    <select
                      value={horaire.jour}
                      onChange={(event) =>
                        updateHoraire(index, "jour", event.target.value)
                      }
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    >
                      <option value="">Jour</option>
                      {WEEK_DAYS.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>

                    <input
                      type="time"
                      value={horaire.debut}
                      onChange={(event) =>
                        updateHoraire(index, "debut", event.target.value)
                      }
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />

                    <input
                      type="time"
                      value={horaire.fin}
                      onChange={(event) =>
                        updateHoraire(index, "fin", event.target.value)
                      }
                      className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />

                    {horaires.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeHoraire(index)}
                        className="inline-flex items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 transition hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                        title="Supprimer cet horaire"
                      >
                        <FaTrash />
                      </button>
                    ) : null}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addHoraire}
                  disabled={horaires.length >= 6}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  + Ajouter un horaire
                </button>
              </div>
            </SectionCard>

            <SectionCard
              title="Tranche d’âge"
              icon={<FaUsers />}
              description="Choisissez des années de naissance précises ou un âge minimum."
            >
              <div className="mb-4 flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  <input
                    type="radio"
                    name="modeAge"
                    checked={modeAge === "annees"}
                    onChange={() => setModeAge("annees")}
                  />
                  Sélectionner des années
                </label>

                <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  <input
                    type="radio"
                    name="modeAge"
                    checked={modeAge === "ageMin"}
                    onChange={() => setModeAge("ageMin")}
                  />
                  Âge minimum
                </label>
              </div>

              {modeAge === "annees" ? (
                <div className="grid max-h-52 gap-2 overflow-y-auto pr-1 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7">
                  {listeAnnees.map((year) => (
                    <label
                      key={year}
                      className={`flex items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-bold transition ${
                        anneesSelectionnees.includes(year)
                          ? "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
                          : "border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={anneesSelectionnees.includes(year)}
                        onChange={(event) =>
                          toggleYear(year, event.target.checked)
                        }
                      />
                      {year}
                    </label>
                  ))}
                </div>
              ) : (
                <Field label="Âge minimum">
                  <input
                    type="number"
                    min={1}
                    value={ageMin}
                    onChange={(event) => setAgeMin(event.target.value)}
                    placeholder="Ex : 14"
                    className="w-full max-w-xs rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>
              )}
            </SectionCard>

            <SectionCard
              title="Tarifs, quota et visibilité"
              icon={<FaEuroSign />}
              description="Définissez les tarifs du groupe, le nombre de places et son affichage à l’inscription."
            >
              <div className="grid gap-4 lg:grid-cols-4">
                <Field label="Tarif licence (€)">
                  <input
                    type="number"
                    name="tarif_licence"
                    min="0"
                    step="0.01"
                    value={formData.tarif_licence}
                    onChange={handleChange}
                    placeholder="Ex : 40"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>

                <Field label="Tarif cotisation (€)">
                  <input
                    type="number"
                    name="tarif_cotisation"
                    min="0"
                    step="0.01"
                    value={formData.tarif_cotisation}
                    onChange={handleChange}
                    placeholder="Ex : 200"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>

                <Field label="Quota">
                  <input
                    type="number"
                    name="quota_groupe"
                    min="0"
                    step="1"
                    value={formData.quota_groupe}
                    onChange={handleChange}
                    placeholder="Ex : 25"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>

                <Field label="Ordre" icon={<FaListOl />}>
                  <input
                    type="number"
                    name="ordre_groupe"
                    min="0"
                    step="1"
                    value={formData.ordre_groupe}
                    onChange={handleChange}
                    placeholder="Ordre"
                    className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                  />
                </Field>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                <label className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                  <span>
                    <span className="block text-sm font-bold text-slate-900 dark:text-white">
                      Groupe visible
                    </span>

                    <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-300">
                      Si désactivé, le groupe est masqué sur les pages
                      publiques.
                    </span>
                  </span>

                  <input
                    type="checkbox"
                    name="is_active"
                    checked={toBool(formData.is_active)}
                    onChange={handleChange}
                    className="h-5 w-5 accent-indigo-600"
                  />
                </label>

                <label className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
                  <span>
                    <span className="block text-sm font-bold text-slate-900 dark:text-white">
                      Inscription ouverte
                    </span>

                    <span className="mt-1 block text-xs leading-5 text-slate-600 dark:text-slate-300">
                      Si désactivé, le groupe reste visible mais l’inscription
                      est bloquée.
                    </span>
                  </span>

                  <input
                    type="checkbox"
                    name="inscription_active"
                    checked={toBool(formData.inscription_active)}
                    onChange={handleChange}
                    className="h-5 w-5 accent-emerald-600"
                  />
                </label>
              </div>
            </SectionCard>

            <SectionCard
              title="Enseignants"
              icon={<FaUserTie />}
              description="Vous pouvez rattacher jusqu’à 6 enseignants à un groupe."
            >
              <div className="space-y-3">
                {enseignantsList.map((value, index) => (
                  <div key={index} className="flex gap-2">
                    <select
                      value={value}
                      onChange={(event) =>
                        updateEnseignant(index, event.target.value)
                      }
                      className="flex-1 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    >
                      <option value="">
                        Sélectionner enseignant {index + 1}
                      </option>
                      {enseignants.map((enseignant) => (
                        <option
                          key={enseignant.id}
                          value={`${enseignant.nom} ${enseignant.prenom}`}
                        >
                          {enseignant.nom} {enseignant.prenom}
                        </option>
                      ))}
                    </select>

                    {enseignantsList.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeEnseignant(index)}
                        className="inline-flex items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 transition hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                      >
                        <FaTrash />
                      </button>
                    ) : null}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addEnseignant}
                  disabled={enseignantsList.length >= 6}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  + Ajouter un enseignant
                </button>
              </div>
            </SectionCard>

            <SectionCard
              title="Couleur et informations"
              icon={<FaInfoCircle />}
              description="La couleur permet d’identifier rapidement le groupe sur les pages publiques et administratives."
            >
              <div className="mb-5">
                <p className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Couleur du groupe
                </p>

                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => {
                    const selected = formData.color_groupe === color.value;

                    return (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => handleColorChange(color.value)}
                        title={color.label}
                        className={`h-9 w-9 rounded-2xl border-2 ring-1 ring-slate-200 transition dark:ring-slate-700 ${
                          color.value
                        } ${
                          selected
                            ? "scale-110 border-slate-950 shadow-md dark:border-white"
                            : "border-white/70"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  Infos complémentaires du groupe
                </p>

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    value={infoGroupe}
                    onChange={setInfoGroupe}
                    theme="snow"
                    placeholder="Informations supplémentaires sur le groupe..."
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    className="[&_.ql-container]:min-h-[160px] [&_.ql-container]:max-h-[320px] [&_.ql-container]:overflow-y-auto"
                  />
                </div>
              </div>
            </SectionCard>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}
